import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { BrandMark } from "@/components/BrandMark";

/**
 * Hero – Desktop: Text links + großes Logo rechts
 * Mobile: Logo oben, dann Text – kompakter und klarer
 */
export const Hero = () => {
  return (
    <section
      id="start"
      data-testid="hero-section"
      className="relative overflow-hidden pt-[calc(3.5rem+env(safe-area-inset-top,0px))] sm:pt-[calc(4rem+env(safe-area-inset-top,0px))]"
    >
      {/* Diagonale Farbfläche */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -right-[18%] top-0 h-[120%] w-[72%] origin-top-right skew-x-[-12deg] bg-[hsl(var(--brand-steelblue))] opacity-[0.09]" />
        <div className="absolute -right-[8%] top-[8%] h-[100%] w-[48%] origin-top-right skew-x-[-12deg] bg-[hsl(var(--brand-steelblue))] opacity-[0.06]" />
        <div className="absolute right-[38%] top-0 h-full w-px origin-top skew-x-[-12deg] bg-[hsl(var(--brand-steelblue))] opacity-20" />
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[hsl(var(--brand-paper))] to-transparent" aria-hidden="true" />

      <div className="site-container relative">
        {/* ── Mobile Layout ── */}
        <div className="flex flex-col items-center pb-12 pt-4 lg:hidden">
          <Reveal>
            <div className="mb-6 flex justify-center">
              <BrandMark size={200} className="drop-shadow-sm" />
            </div>
          </Reveal>

          <Reveal delay={0.06}>
            <div className="w-full text-center">
              <h1 className="text-[2rem] font-semibold leading-[1.08] tracking-[-0.03em] text-foreground sm:text-4xl">
                Schweißarbeiten,
                <br />
                <span className="italic text-[hsl(var(--brand-steelblue))]">die halten.</span>
              </h1>

              <p className="mx-auto mt-4 max-w-[34ch] text-[15px] leading-relaxed text-muted-foreground">
                Unser eigenes Team übernimmt mobile Einsätze, Projekte und Reparaturen – präzise und termintreu.
              </p>

              <div className="mt-7 flex flex-col items-center gap-3">
                <Button
                  asChild
                  size="lg"
                  className="min-h-[48px] w-full max-w-xs rounded-full px-7 active:scale-[0.98]"
                  data-testid="hero-primary-cta-button"
                >
                  <a href="/#kontakt">
                    Projekt anfragen
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </Button>
                <a
                  href="/#prozess"
                  data-testid="hero-secondary-link"
                  className="text-sm font-medium text-[hsl(var(--brand-steelblue))] underline-offset-4 hover:underline"
                >
                  Wie wir arbeiten
                </a>
              </div>
            </div>
          </Reveal>
        </div>

        {/* ── Desktop Layout ── */}
        <div className="hidden min-h-[min(72vh,700px)] items-center gap-6 pb-24 pt-12 lg:grid lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal>
              <h1 className="text-4xl font-semibold leading-[1.04] tracking-[-0.03em] text-foreground sm:text-5xl lg:text-[3.4rem]">
                Schweißarbeiten,
                <br />
                <span className="italic text-[hsl(var(--brand-steelblue))]">die halten.</span>
              </h1>

              <p className="mt-6 max-w-[38ch] text-base leading-relaxed text-muted-foreground sm:text-lg">
                Unser eigenes Team übernimmt mobile Einsätze, Projekte und
                Reparaturen – präzise, termintreu und ohne Umwege.
              </p>

              <div className="mt-9 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                <Button
                  asChild
                  size="lg"
                  className="min-h-[48px] rounded-full px-7 shadow-[0_10px_24px_rgba(15,23,42,0.08)] transition-colors duration-200 active:scale-[0.98]"
                >
                  <a href="/#kontakt">
                    Projekt anfragen
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </Button>
                <a
                  href="/#prozess"
                  className="text-sm font-medium text-[hsl(var(--brand-steelblue))] underline-offset-4 hover:underline"
                >
                  Wie wir arbeiten
                </a>
              </div>
            </Reveal>
          </div>

          <div className="flex justify-end lg:col-span-6">
            <Reveal delay={0.08}>
              <div className="relative -mr-12">
                <BrandMark size={380} className="drop-shadow-sm" />
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
